// The workbench mode-tab strip (Board / Types / Process / Docs) — the shared
// config both the viewer's WorkspacePanel and the Studio rail mount on
// WorkspaceNav's `tabs` prop. Lives here (not inside WorkspacePanel) so Studio
// reuses it without pulling in the panel.
import type { ReactNode } from "react";
import { LayoutGrid, Braces, FileText, Eye } from "lucide-react";
import { ProcessGlyph } from "../primitives/icons.js";
import type { Mode } from "../../hooks/useMode";
import type { HostNavTab, NavView } from "../primitives/WorkspaceNav";

export const MODE_TABS: ReadonlyArray<{
  id: Mode;
  label: string;
}> = [
  { id: "board", label: "Board" },
  { id: "types", label: "Types" },
  { id: "process", label: "Process" },
  { id: "views", label: "Views" },
  { id: "docs", label: "Docs" },
];

/** Mode icons (lucide) — board / types / process / views / docs. */
export function ModeIcon({ id }: { id: Mode }): ReactNode {
  const props = {
    className: "h-5 w-5",
    strokeWidth: 1.8,
    "aria-hidden": true,
  } as const;
  switch (id) {
    case "board":
      return <LayoutGrid {...props} />;
    case "types":
      return <Braces {...props} />;
    case "process":
      return <ProcessGlyph {...props} />;
    case "views":
      return <Eye {...props} />;
    default: // docs
      return <FileText {...props} />;
  }
}

/** Build WorkspaceNav's `tabs` prop for the mode strip.
 *
 *  Every mode tab is always CLICKABLE and looks identical whether or not it has
 *  content — an empty Process/Views tab opens onto its panel's empty state
 *  instead of being a dead greyed button (DESIGN-SYSTEM.md §2 "Tab handles").
 *  Emptiness is stated by the panel body, never by the handle, so nothing here
 *  varies with `hasProcesses`/`hasViews`; `disabled` stays reserved for
 *  genuinely unavailable tabs (no permission / not loaded). */
export function buildModeTabs({
  active,
  onSelect,
}: {
  readonly active: Mode;
  readonly onSelect: (id: Mode) => void;
}): {
  readonly items: readonly HostNavTab[];
  readonly active: string;
  readonly onSelect: (id: string) => void;
} {
  return {
    items: MODE_TABS.map((t) => ({
      id: t.id,
      label: t.label,
      icon: <ModeIcon id={t.id} />,
    })),
    active,
    onSelect: (id) => onSelect(id as Mode),
  };
}

/** The nav list a given mode tab shows. */
export function navViewForMode(navTab: Mode): NavView {
  return navTab === "types"
    ? "types"
    : navTab === "process"
      ? "processes"
      : navTab === "views"
        ? "views"
        : navTab === "docs"
          ? "documents"
          : "files";
}
